import * as React from 'react';
import {
  Grid,
  CardHeader,
  CardContent,
  Box,
  Paper,
  Button,
  TextField,
  IconButton,
  FormControlLabel,
  Radio,
  RadioGroup,
  createStyles,
  makeStyles,
  Typography,
  Avatar,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import { DeleteOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import type { IWorkViewDetail } from '../WorkViewInterfaces';
import theme from '../../../../theme';
import type ILabelEnumPair from '../../../global/GlobalInterfaces';
import type { ILabelValuePair } from '../../../marking/MarkingInterfaces';
import CopyIcon from '../../../../assets/icons/Copy.svg';
import globalUseStyles from '../../../../globalClasses';

interface IEditWorkViewSets {
  setFormValues: (values: IWorkViewDetail) => void;
  formValues: IWorkViewDetail;
  WorkViewSetsDisplayStyles?: ILabelEnumPair[];
  Bins?: ILabelValuePair[];
  primaryGroupIndex: number;
  handleAddNewWorkViewSet: (primaryGroupIndex: number, workViewIndex: number) => void;
  handleWorkViewSetColorChange: (primaryGroupIndex: number, workViewIndex: number, event: React.ChangeEvent<HTMLInputElement>) => void;
  removeWorkViewSet: (primaryGroupIndex: number, workViewIndex: number) => void;
  duplicateWorkViewSet: (primaryGroupIndex: number, workViewIndex: number) => void;
}

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  whiteBackgrounds: {
    backgroundColor: 'white',
  },
  workViewSetColorRadioButtons: {
    '& .MuiFormGroup-root, .MuiFormControlLabel-root, label': {
      marginBottom: '0',
    },
  },
  deleteIcon: {
    padding: 0,
    maxHeight: '15px',
    maxwidth: '10.5px',
    marginBottom: 2.5,
  },
  duplicateIcon: {
    height: 16.5,
    width: 14.25,
  },
}));

const WorkViewSets: React.FC<IEditWorkViewSets> = ({
  setFormValues, formValues, WorkViewSetsDisplayStyles, Bins, primaryGroupIndex, handleAddNewWorkViewSet, handleWorkViewSetColorChange, removeWorkViewSet, duplicateWorkViewSet,
}) => {
  const classes = useStyles();
  const globalStyles = globalUseStyles();
  const { enqueueSnackbar } = useSnackbar();

  return (
    <Grid>
      <Grid>
        <CardHeader title="Create Set(s) in Order" />
        {formValues.primaryGroups[primaryGroupIndex].sets?.map((workViewSet, workViewSetIndex) => (
          <>
            <CardContent>
              <Paper style={{ backgroundColor: 'rgba(251, 251, 251)' }}>
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Box ml={2} my={2}>
                      <Typography style={{ fontWeight: '700', fontSize: '16px', lineHeight: '18.75px' }}>
                        {'Set #'}
                        {workViewSetIndex + 1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Grid container alignItems="center" spacing={1} style={{ padding: '10px 17.5px' }}>
                      <Grid item>
                        <IconButton className={classes.duplicateIcon} onClick={() => duplicateWorkViewSet(primaryGroupIndex, workViewSetIndex)}>
                          <img src={CopyIcon} alt="CopyIcon.svg" />
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <IconButton className={classes.deleteIcon} onClick={() => removeWorkViewSet(primaryGroupIndex, workViewSetIndex)}>
                          <Avatar className={clsx(globalStyles.smallAvatar)}>
                            <DeleteOutlined className={globalStyles.redIconReverse} />
                          </Avatar>
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    <Box p={2}>
                      <TextField
                        className={classes.whiteBackgrounds}
                        value={workViewSet?.name}
                        id="workViewSet-value-input"
                        label="Set Name"
                        variant="outlined"
                        type="string"
                        fullWidth
                        inputProps={{ maxLength: 50 }}
                        onChange={(event) => {
                          const inputVal = event.target.value;
                          if (inputVal.length >= 50) {
                            enqueueSnackbar('Set Name cannot be more than 50 characters long.', { persist: false, variant: 'error' });
                          }
                          const tempPrimaryGroups = formValues.primaryGroups;
                          tempPrimaryGroups[primaryGroupIndex].sets[workViewSetIndex].name = inputVal;
                          setFormValues({ ...formValues, primaryGroups: tempPrimaryGroups });
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box p={2}>
                      {WorkViewSetsDisplayStyles && (
                        <Autocomplete
                          className={classes.whiteBackgrounds}
                          value={workViewSet?.displayStyle}
                          onChange={(event: unknown, selectedLayout: ILabelEnumPair | null) => {
                            if (selectedLayout) {
                              const tempPrimaryGroups = formValues.primaryGroups;
                              tempPrimaryGroups[primaryGroupIndex].sets[workViewSetIndex].displayStyle = { label: selectedLayout?.label, value: selectedLayout?.value };
                              setFormValues({ ...formValues, primaryGroups: tempPrimaryGroups });
                            } else {
                              const tempPrimaryGroups = formValues.primaryGroups;
                              tempPrimaryGroups[primaryGroupIndex].sets[workViewSetIndex].displayStyle = { label: '', value: 0 };
                              setFormValues({ ...formValues, primaryGroups: tempPrimaryGroups });
                            }
                          }}
                          options={WorkViewSetsDisplayStyles}
                          autoHighlight
                          autoSelect
                          getOptionLabel={(option) => option.label.toString()}
                          // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          renderInput={(params) => <TextField {...params} label="Display Style" id="select-WorkViewSet-displayStyle-autocomplete" variant="outlined" />}
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <RadioGroup
                        aria-label="isColor"
                        name="isColor"
                        value={workViewSet?.color === true ? 'true' : 'false'}
                        onChange={(event) => handleWorkViewSetColorChange(primaryGroupIndex, workViewSetIndex, event)}
                      >
                        <FormControlLabel
                          value="true"
                          label="Color"
                          className={classes.workViewSetColorRadioButtons}
                          control={<Radio color="primary" size="small" id="color-set-radio-button" />}
                        />
                        <FormControlLabel
                          value="false"
                          label="No Color"
                          control={<Radio color="primary" size="small" id="nocolor-set-radio-button" />}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={2} pt={0}>
                      {Bins && (
                        <Autocomplete
                          className={classes.whiteBackgrounds}
                          key={JSON.stringify(workViewSet.bins)}
                          defaultValue={workViewSet.bins}
                          multiple
                          onChange={(event, selectedOptions: ILabelValuePair[]) => {
                            const tempFormValues = formValues.primaryGroups;
                            tempFormValues[primaryGroupIndex].sets[workViewSetIndex].bins = selectedOptions;
                            setFormValues({ ...formValues, primaryGroups: tempFormValues });
                          }}
                          autoHighlight
                          autoSelect
                          options={Bins.sort((a, b) => -b.label?.localeCompare(a.label))}
                          getOptionLabel={(option) => option.label}
                          // JAG MUI out of the box AutoComplete functionality requires prop spreading.
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          renderInput={(params) => <TextField {...params} label="Search and Add Bins" id="add-Bins-autocomplete" variant="outlined" />}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              <Grid item xs={12} container justifyContent="flex-start">
                <Grid item xs={6}>
                  <Box pt={1}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => { handleAddNewWorkViewSet(primaryGroupIndex, workViewSetIndex); }}
                      style={{ minWidth: '7rem', float: 'left', marginLeft: '-20px' }}
                    >
                      <IconButton disableFocusRipple disabled disableRipple>
                        <AddCircleIcon style={{ color: theme.palette.success.main, float: 'right' }} fontSize="large" />
                      </IconButton>
                      {'Add Set'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
              <Grid>
                <Box p={1} />
              </Grid>
            </CardContent>
          </>
        ))}
      </Grid>
    </Grid>
  );
};

export default WorkViewSets;
